import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Card, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime, daysSince, firstYearValue, money, monthLabel } from '@/lib/format';
import { addDealNote, getDeal, getPipelines, unlinkDealLead, updateDeal } from '@/data/client';
import type { DealDetail as DealDetailT, DraftKind } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { BUCKET_LABEL } from '@/components/crm/SourceBadge';
import { useDealMover } from '@/components/crm/DealMove';
import { DealFormModal } from '@/components/crm/DealForm';
import { FormError } from '@/components/crm/Modal';
import { LeadDrawer } from '@/components/crm/LeadDrawer';
import { NoteComposer, Timeline } from '@/components/crm/Timeline';
import { MeetingsPanel } from '@/components/crm/MeetingsPanel';
import { FollowUpCard } from '@/components/crm/FollowUp';
import { CoachCard } from '@/components/crm/CoachCard';
import { DraftModal, EmailAssistantCard } from '@/components/crm/EmailAssistant';
import { ClientVault } from '@/components/vault/ClientVault';

export function DealDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { me } = useMe();
  const { data, isLoading } = useQuery({ queryKey: ['deal', id], queryFn: () => getDeal(id) });
  const { data: pipelines } = useQuery({ queryKey: ['pipelines'], queryFn: getPipelines, staleTime: 10 * 60_000 });
  const [editing, setEditing] = useState(false);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [tab, setTab] = useState<'timeline' | 'vault'>('timeline');
  const [drafting, setDrafting] = useState<{ kind: DraftKind; instructions?: string } | null>(null);
  const mover = useDealMover();
  const refresh = () => { for (const k of ['deal', 'deals', 'leads', 'leadSummary', 'coach']) void qc.invalidateQueries({ queryKey: [k] }); };
  const unlink = useMutation({ mutationFn: (leadId: string) => unlinkDealLead(id, leadId), onSuccess: refresh });
  const confirmValue = useMutation({ mutationFn: () => updateDeal(id, { valueUnconfirmed: false }), onSuccess: refresh });

  if (isLoading) return null;
  if (!data) return <div className="dash-empty"><h2>Deal not found</h2><Link to="/pipeline">Back to pipeline</Link></div>;
  const { deal, leads, stageHistory, timeline, companyContacts } = data;
  const stages = pipelines?.find((p) => p.id === deal.pipelineId)?.stages ?? [];
  const canRelink = !deal.attributionLocked || !!me?.isAdmin;

  return (
    <div className="crm-record">
      <Link to="/pipeline" className="cq-back"><Icon name="arrow-left" size={16} /> Pipeline</Link>
      <div className="crm-record__head">
        <div className="crm-record__title">
          <h2>
            {deal.name}
            {deal.status !== 'open' && <Badge variant={deal.status === 'won' ? 'success' : 'neutral'}>{deal.status === 'won' ? 'Won' : 'Lost'}</Badge>}
            {deal.stageNurture && <Badge variant="outline">Nurture</Badge>}
          </h2>
          <div className="crm-record__sub">
            <Link to={`/companies/${deal.companyId}`}>{deal.companyName}</Link>
            {deal.contactName && <span>{deal.contactName}</span>}
            <span>Owner: {deal.ownerName ?? 'Unassigned'}</span>
            {deal.importSource === 'legacy_crm' && <span title="Created and closed dates come from the old CRM">Imported from the old CRM{deal.legacyOwner && deal.legacyOwner !== deal.ownerName ? ` · owner there: ${deal.legacyOwner}` : ''}</span>}
          </div>
        </div>
        <div className="crm-record__actions">
          <select className="crm-inline-select crm-inline-select--lg" aria-label="Stage" value={deal.stageId}
            onChange={(e) => { const s = stages.find((x) => x.id === e.target.value); if (s) mover.request(deal, s); }}>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button variant="outline" size="sm" iconLeft={<Icon name="edit" size={14} />} onClick={() => setEditing(true)}>Edit</Button>
        </div>
      </div>
      <FormError error={mover.error} />
      {deal.valueUnconfirmed && (
        <div className="crm-callout">
          <strong>Value not confirmed.</strong> {money(deal.mrr)}/mo came from the old CRM, which often held a $500 placeholder. Edit the value, or confirm it's right.
          <div style={{ marginTop: 8 }}>
            <Button size="sm" variant="outline" disabled={confirmValue.isPending} onClick={() => confirmValue.mutate()}>Confirm value</Button>
          </div>
          <FormError error={confirmValue.error} />
        </div>
      )}
      {deal.status === 'lost' && deal.lostReason && (
        <p className="crm-callout">Lost — {deal.lostReason}{deal.lostNote ? `: ${deal.lostNote}` : ''}</p>
      )}

      <CoachCard dealId={deal.id} onDraft={(kind, instructions) => setDrafting({ kind, instructions })} />

      <div className="crm-record__cols">
        <div className="crm-list">
          <div className="lead-tabsbar">
            <Tabs items={[{ value: 'timeline', label: 'Timeline' }, { value: 'vault', label: 'Client Vault' }]} value={tab} onChange={(v) => setTab(v as 'timeline' | 'vault')} />
          </div>
          {tab === 'timeline' ? (
            <Card padding={false} className="crm-section">
              <NoteComposer onSave={(body) => addDealNote(id, body).then(refresh)} />
              <Timeline items={timeline} filterable />
            </Card>
          ) : <ClientVault companyId={deal.companyId} companyName={deal.companyName} dealId={deal.id} />}
        </div>

        <div className="crm-record__side">
          <FollowUpCard deal={deal} />
          <EmailAssistantCard nurture={deal.stageNurture} hasInbound={timeline.some((a) => a.type === 'email')} onDraft={(kind) => setDrafting({ kind })} />
          <MeetingsPanel filter={{ dealId: deal.id }} companyId={deal.companyId} dealId={deal.id} defaultTitle={`${deal.companyName} — ${deal.stageName}`}
            guests={companyContacts.map((p) => ({ contactId: p.id, name: p.name, email: p.email }))} />

          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Value</h3></div>
            <dl className="crm-fields">
              <dt>Retainer</dt><dd>{deal.mrr != null ? `${money(deal.mrr)}/mo` : '—'}</dd>
              <dt>Setup fee</dt><dd>{money(deal.setupFee)}</dd>
              <dt>Term</dt><dd>{deal.contractMonths ? `${deal.contractMonths} months` : 'Month to month'}</dd>
              <dt>First year</dt><dd>{deal.mrr || deal.setupFee ? money(firstYearValue(deal.mrr, deal.setupFee, deal.contractMonths)) : '—'}</dd>
              {me?.workspace.trackMargin && <><dt>Margin</dt><dd>{deal.grossMarginPct != null ? `${deal.grossMarginPct}%` : '—'}</dd></>}
              <dt>Strategy fee</dt><dd>{deal.strategyFeeAmount != null ? `${money(deal.strategyFeeAmount)} (credited if won)` : '—'}</dd>
            </dl>
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head">
              <h3>Attribution</h3>
              {deal.attributionLocked && <span className="crm-cell-sub" title="Locked when the deal was won"><Icon name="lock" size={12} /> Locked</span>}
            </div>
            <Attribution data={data} onOpenLead={setOpenLead} />
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Leads ({leads.length})</h3></div>
            {leads.length ? (
              <ul className="crm-people">
                {leads.map((l) => (
                  <li key={l.id}>
                    <div>
                      <button type="button" className="crm-linkbtn" onClick={() => setOpenLead(l.id)}>{l.name}</button>
                      <span className="crm-cell-sub">
                        {BUCKET_LABEL[l.sourceBucket]}{l.sourceLabel || l.utmCampaign ? ` · ${l.sourceLabel ?? l.utmCampaign}` : ''} · {dateTime(l.receivedAt)}
                        {l.id === deal.creditedLeadId ? ' · credited' : ''}
                      </span>
                    </div>
                    {canRelink && (
                      <button type="button" className="lead-modal__x" aria-label={`Unlink ${l.name}`} title="Unlink — returns the lead to the inbox"
                        onClick={() => unlink.mutate(l.id)}><Icon name="x" size={14} /></button>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p className="crm-cell-muted">No leads — this deal reports as unattributed. Link one from the leads inbox.</p>}
            <FormError error={unlink.error} />
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Stage history</h3></div>
            <ol className="crm-history">
              {stageHistory.map((h, i) => {
                const next = stageHistory[i + 1]?.at;
                const days = next ? Math.max(0, Math.floor((Date.parse(next) - Date.parse(h.at)) / 86_400_000)) : daysSince(h.at);
                return (
                  <li key={i}>
                    <strong>{h.to}</strong>
                    <span className="crm-cell-sub">{dateTime(h.at)}{h.userName ? ` · ${h.userName}` : ''} · {days}d{next ? '' : ' so far'}</span>
                  </li>
                );
              })}
            </ol>
          </Card>
        </div>
      </div>

      {mover.ui}
      {editing && <DealFormModal deal={deal} contacts={companyContacts} onClose={() => setEditing(false)} />}
      {openLead && <LeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} />}
      {drafting && <DraftModal dealId={deal.id} dealName={deal.companyName} kind={drafting.kind} instructions={drafting.instructions} onClose={() => setDrafting(null)} />}
    </div>
  );
}

/* First touch is the reporting default and the lead credited with the revenue;
   last touch is shown beside it when it differs. Cohort = lead month. */
function Attribution({ data, onOpenLead }: { data: DealDetailT; onOpenLead: (id: string) => void }) {
  const { deal, leads } = data;
  if (!deal.creditedLeadId) return <p className="crm-cell-muted">Unattributed — no lead is linked.</p>;
  const first = leads.find((l) => l.id === deal.creditedLeadId);
  const last = leads.find((l) => l.id === deal.lastTouchLeadId);
  const describe = (l: typeof first) => l ? `${BUCKET_LABEL[l.sourceBucket]}${l.sourceLabel || l.utmCampaign ? ` · ${l.sourceLabel ?? l.utmCampaign}` : ''}` : '—';
  const closeDays = deal.closedAt && deal.cohortDate ? Math.round((Date.parse(deal.closedAt) - Date.parse(deal.cohortDate)) / 86_400_000) : null;
  return (
    <dl className="crm-fields">
      <dt>First touch</dt>
      <dd><button type="button" className="crm-linkbtn" onClick={() => first && onOpenLead(first.id)}>{describe(first)}</button></dd>
      {last && last.id !== first?.id && <>
        <dt>Last touch</dt>
        <dd><button type="button" className="crm-linkbtn" onClick={() => onOpenLead(last.id)}>{describe(last)}</button></dd>
      </>}
      <dt>Lead month</dt><dd>{monthLabel(deal.cohortDate)}</dd>
      {deal.closedAt && <><dt>Closed</dt><dd>{monthLabel(deal.closedAt)}{closeDays != null ? ` · ${closeDays} days from lead` : ''}</dd></>}
      {first?.hasGclid && <><dt>Google Ads</dt><dd>Click id captured</dd></>}
    </dl>
  );
}
